import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { createPublicClient, http, parseEther } from "viem";
import { CONTRACTS } from "@/lib/bridge/contracts";

// Camp Network RPC and chain config
const CAMP_CHAIN_ID = 484;
const CAMP_RPC = "https://rpc-mainnet.campnetwork.xyz";

// LayerZero EIDs
const ETHEREUM_MAINNET_EID = 30101; // Destination EID

// Known adapter used by the app for quotes (from useBridge.ts)
const CAMP_ADAPTER_ADDRESS = "0x40368666eff4Add443cEf30BBAd2eAcAe5F2cbE8";

const TOKEN_ADAPTER_ADDRESS = {
  eth: CONTRACTS.ETH_OFT_TOKEN.address as `0x${string}`,
  usdc: CONTRACTS.USDC_OFT_TOKEN.address as `0x${string}`,
  camp: CAMP_ADAPTER_ADDRESS as `0x${string}`,
};

// Minimal ABI fragments required for quoteSend and removeDust
const ADAPTER_ABI = [
  {
    inputs: [
      {
        components: [
          { internalType: "uint32", name: "dstEid", type: "uint32" },
          { internalType: "bytes32", name: "to", type: "bytes32" },
          { internalType: "uint256", name: "amountLD", type: "uint256" },
          { internalType: "uint256", name: "minAmountLD", type: "uint256" },
          { internalType: "bytes", name: "extraOptions", type: "bytes" },
          { internalType: "bytes", name: "composeMsg", type: "bytes" },
          { internalType: "bytes", name: "oftCmd", type: "bytes" },
        ],
        internalType: "struct SendParam",
        name: "_sendParam",
        type: "tuple",
      },
      { internalType: "bool", name: "_payInLzToken", type: "bool" },
    ],
    name: "quoteSend",
    outputs: [
      {
        components: [
          { internalType: "uint256", name: "nativeFee", type: "uint256" },
          { internalType: "uint256", name: "lzTokenFee", type: "uint256" },
        ],
        internalType: "struct MessagingFee",
        name: "msgFee",
        type: "tuple",
      },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "_amountLD", type: "uint256" }],
    name: "removeDust",
    outputs: [{ internalType: "uint256", name: "amountLD", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
] as const;

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

function addressToBytes32(addr: string): string {
  if (!/^0x[0-9a-fA-F]{40}$/.test(addr)) {
    throw new Error("Invalid address");
  }
  return "0x" + "0".repeat(24) + addr.slice(2).toLowerCase();
}

// Build LayerZero options for gas limit
function buildLzOptions(gasLimit: bigint): string {
  const gasHex = gasLimit.toString(16).padStart(8, "0");
  return "0x000301001101000000000000000000000000" + gasHex;
}

export interface FeeEstimateParams {
  to: string;
  amount: string; // amount in CAMP (18 decimals)
  slippageBps?: bigint; // slippage in basis points, defaults to 50 (0.5%)
  gasLimit?: bigint; // gas limit, defaults to 3000000,
  tokenId: string;
}

export interface FeeEstimateResult {
  params: {
    to: string;
    amountWei: string;
    dustAdjustedAmount: string;
    minAmountWei: string;
    gasLimit: string;
    lzOptions: string;
  };
  quote: {
    nativeFee: string;
    lzTokenFee: string;
  };
}

export async function estimateLayerZeroFee({
  to,
  amount,
  tokenId,
  slippageBps = 50n, // 0.5%
  gasLimit = 3000000n, // 3,000,000
}: FeeEstimateParams): Promise<FeeEstimateResult> {
  const publicClient = createPublicClient({
    chain: {
      id: CAMP_CHAIN_ID,
      name: "Camp Network",
      nativeCurrency: { name: "CAMP", symbol: "CAMP", decimals: 18 },
      rpcUrls: { default: { http: [CAMP_RPC] } },
    },
    transport: http(CAMP_RPC),
  });

  const amountWei = parseEther(amount);
  const minAmountWei = amountWei - (amountWei * slippageBps) / 10000n;
  const lzOptions = buildLzOptions(gasLimit);

  // Optional: remove dust adjustment if adapter exposes it
  let dustAdjustedAmount = amountWei;
  try {
    const res = await publicClient.readContract({
      address: TOKEN_ADAPTER_ADDRESS[tokenId as keyof typeof TOKEN_ADAPTER_ADDRESS],
      abi: ADAPTER_ABI,
      functionName: "removeDust",
      args: [amountWei],
    }) as bigint;
    dustAdjustedAmount = res;
  } catch {
    // ignore if not available
  }

  const sendParam = {
    dstEid: ETHEREUM_MAINNET_EID,
    to: addressToBytes32(to) as `0x${string}`,
    amountLD: dustAdjustedAmount,
    minAmountLD: minAmountWei,
    extraOptions: lzOptions as `0x${string}`,
    composeMsg: "0x" as `0x${string}`,
    oftCmd: "0x" as `0x${string}`,
  };

  const result = await publicClient.readContract({
    address: TOKEN_ADAPTER_ADDRESS[tokenId as keyof typeof TOKEN_ADAPTER_ADDRESS],
    abi: ADAPTER_ABI,
    functionName: "quoteSend",
    args: [sendParam, false],
  }) as { nativeFee: bigint; lzTokenFee: bigint };

  const { nativeFee, lzTokenFee } = result;

  return {
    params: {
      to,
      amountWei: amountWei.toString(),
      dustAdjustedAmount: dustAdjustedAmount.toString(),
      minAmountWei: minAmountWei.toString(),
      gasLimit: gasLimit.toString(),
      lzOptions,
    },
    quote: {
      nativeFee: nativeFee.toString(),
      lzTokenFee: lzTokenFee.toString(),
    },
  };
}
